import "./RegistrationForm.css";
import { useParams } from "react-router-dom";
import Select from "../Select/Select";
import * as translator from "../../utils/translator";
import * as http from "../../utils/http";
import { validateEmail, validateName } from "../../utils/fieldsValidator";
import SubmitButton from "../SubmitButton/SubmitButton";
import Checkbox from "../Checkbox/Checkbox";
import InputField from "../CredentialsInput/InputField";
import BinarySelectV2025 from "../BinarySelect/BinarySelectV2025.jsx";
import { useState, useEffect ,useRef } from "react";
import { deRegistrationForm, frRegistrationForm } from "./constants";
import useForm from "../../customHooks/useForm";
import ReCAPTCHA from "react-google-recaptcha";
import { clientConfig } from "../../config";
import { useCartContext } from "../../customHooks/useCart";
import { REGISTRATION_COMPLETED_EVENT, EMAIL_MISMATCH_ERROR_EVENT, SIGN_UP_PAGE_VIEW_EVENT, TOOLTIP_MEDICATION, TOOLTIP_SERVICE_FEE, TOOLTIP_TREATMENT_FEE, CLICK_EXPAND_PRODUCTS, REGISTRATION_INVALID_EMAIL_EVENT } from "../../utils/consts";
import * as SocialMediaTracker from "../../PixelTrackers/SocialMediaTracker";
import { useCustomer } from "../../customHooks/useCustomer";
import SocialLoginButtons from "../LoginStrategies/SocialLoginButtons";
import { MainTracker } from "../../PixelTrackers/MainTracker";
import { scrollToFirstErrorAfterRender } from "../../utils/scrollToFirstError";
import DatePickerWrapper from "../DatePickerWrapper/DatePickerWrapper";


/**
 * @param {Object} props
 * @param {Function} props.navigationCallback
 * @param {Function} props.setStickyNotificationDetails
 * @param {string} props.languageProp
 * @param {string} props.submitButtonPlaceholder
 * @param {string} props.redirectPath
 * @param {string} [props.customerVerificationType]
 * @param {string} props.cameFromPath
 * @param {Object} [props.openedTooltipsRef]
 * @param {Object} [props.userClicksRef]
 * @param {boolean} props.isCannabisCatalog
 * @param {string|null} [props.appleSignInSource] - "apple_sign_in" when arriving from Sign in with Apple
 * @param {string} [props.appleSignInEmail]
 * @param {string} [props.appleSignInFirstName]
 * @param {string} [props.appleSignInLastName]
 * @param {string} [props.initialGender="0"] - default gender selection; V3 passes "1" (male)
 * @param {Function} [props.onExistingEmail] - V3 callback when register hits already_exist
 * @param {boolean} [props.showSocialLogin=true] - render embedded social login buttons
 * @param {boolean} [props.birthdayDatePicker=false] - V3: questionnaire date picker instead of day/month/year dropdowns
 */
export default function RegistrationForm({
  navigationCallback,
  setStickyNotificationDetails,
  languageProp,
  submitButtonPlaceholder,
  redirectPath,
  customerVerificationType='',
  cameFromPath,
  openedTooltipsRef,
  userClicksRef,
  isCannabisCatalog,
  appleSignInSource = null,
  appleSignInEmail = "",
  appleSignInFirstName = "",
  appleSignInLastName = "",
  initialGender = "0",
  onExistingEmail,
  showSocialLogin = true,
  birthdayDatePicker = false,
}) {
  let { country, language } = useParams();
  language = language || languageProp; //In funnel with have language from url but in LoginPage we don't so we pass it has prop
  
  const {getSessionFields} = useCartContext();
  const { saveCustomer } = useCustomer();
  const [submitClicked, setSubmitClicked] = useState(false);
  const [submitButtonLoading, setSubmitButtonLoading] = useState(false);
  const [windowSize, setWindowSize] = useState(typeof window !== 'undefined' ? window.innerWidth : 0); 
  const MALE = "1";
  const FEMALE = "0";
  const reRef = useRef();
  const sessionCart = getSessionFields()

  useEffect(() => {
    const handleResize = () => {
      setWindowSize(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  let initialValues;

  if (['de', 'ch', 'uk', 'gb'].includes(country.toLowerCase())) {
    initialValues = { ...deRegistrationForm, category: sessionCart.mention, gender: initialGender };
  } else {
    initialValues = { ...frRegistrationForm, category: sessionCart.mention, gender: initialGender };
  }

  // HL-10259: When arriving from Sign in with Apple, pre-fill the
  // Apple-verified email and name. Email and name are shown as read-only
  // so the customer can see them but not edit them. The customer creates
  // their own password as in the regular registration flow.
  const isAppleSignIn = appleSignInSource === "apple_sign_in";
  // Apple only sends the name on the first authorization, and may send
  // missing/whitespace-only/invalid values. Only lock (disable) a name field
  // when Apple's value is actually valid - otherwise leave it editable and
  // blank so the customer can fill it in, same as the regular flow.
  const isAppleSignInFirstNameValid = isAppleSignIn && validateName(appleSignInFirstName, language).isValid;
  const isAppleSignInLastNameValid = isAppleSignIn && validateName(appleSignInLastName, language).isValid;
  if (isAppleSignIn) {
    initialValues.source = "apple_sign_in";
    if (appleSignInEmail) {
      initialValues.email = appleSignInEmail;
      initialValues.confirm_email = appleSignInEmail;
    }
    if (isAppleSignInFirstNameValid) {
      initialValues.first_name = appleSignInFirstName;
    }
    if (isAppleSignInLastNameValid) {
      initialValues.last_name = appleSignInLastName;
    }
  }

  const registrationFormAttributes = useForm(initialValues, language);
  const formContainerRef = useRef(null);

  useEffect(() => {
    window.addEventListener('resize', setWindowSize(window.innerWidth));
    SocialMediaTracker.track("pv", SIGN_UP_PAGE_VIEW_EVENT, {mention: sessionCart.mention});
  }, []);

  const onSubmit = async () => {
    setSubmitButtonLoading(true);
    setSubmitClicked(true);
    try {
    window.eventsTracking.track({e: "Click registration"});
    // HL-10259: confirm_email is hidden for Apple Sign In — skip validation to avoid false negatives.
    const ignoreValidationFields = isAppleSignIn ? ["confirm_email"] : undefined;
    const isFormValid = registrationFormAttributes.validateForm(ignoreValidationFields);
    if (!isFormValid) {
      const email = registrationFormAttributes.form.email;
      if (email && email.length > 0 && !validateEmail(email, language).isValid) {
        MainTracker.track("custom", REGISTRATION_INVALID_EMAIL_EVENT, {
          "Entered email": email,
        });
      }
      // Bring the first invalid field into view; the red highlighting itself is
      // unchanged, this only scrolls to it.
      scrollToFirstErrorAfterRender(formContainerRef);
      return;
    }
    if (isFormValid) {
      // Track user click breakdown event
      if(isCannabisCatalog && openedTooltipsRef && userClicksRef){
          MainTracker.track("custom", "user click breakdown", {
            "page": "Register",
            "Click on delivery options": false,
            "Click on Medication tooltip": openedTooltipsRef.current.has(TOOLTIP_MEDICATION),
            "Click on Service Fee tooltip": openedTooltipsRef.current.has(TOOLTIP_SERVICE_FEE),
            "Click on Treatment Fee tooltip": openedTooltipsRef.current.has(TOOLTIP_TREATMENT_FEE),
            "Click expand product (show/hide)": userClicksRef.current.has(CLICK_EXPAND_PRODUCTS),
            "Click on Partner Pharmacies pop-up": false,
            "Click on pharmacy details (expand)": false,
            "Click on terms": false,
            "Click on doctor details tooltip": false,
            "Click on product pharmacy tooltip": false
          });
      }
      
      const locale = language.toLowerCase() + "-" + country.toUpperCase();
      const birthday = formatDateToYYYYMMDD(
        registrationFormAttributes.form.year,
        registrationFormAttributes.form.month,
        registrationFormAttributes.form.day
      );
      // ReCaptcha handling
    let captchaToken 
    if (clientConfig.IS_RECAPTCHA_ENABLED){
      captchaToken = await reRef.current.executeAsync();
      reRef.current.reset();
    }
      const trackingData =
        window.eventsTracking && window.eventsTracking.getData();
      const formmatedRegistrationForm = {
        ...registrationFormAttributes.form,
        birthday,
        trackingData,
        locale,
        captchaToken
      };
      await http
        .postToServer(`auth/local/register`, formmatedRegistrationForm, [
          http.MIDDLEWARES.CART_ORDER,
          http.MIDDLEWARES.ENCRYPTED_BODY,
        ])
        .then((response) => {
          if (response.data.status === "ok") {
            SocialMediaTracker.track("pv", REGISTRATION_COMPLETED_EVENT, {"email": formmatedRegistrationForm.email, "email_verified_double_entry": true});
            MainTracker.track("custom", "registration_completed", {
              "email": formmatedRegistrationForm.email,
              "email_verified_double_entry": true
            });
            
            saveCustomer({
              customerID: response.data.customerID,
              uuid: response.data.uuid,
              token: response.data.rememberToken,
              email: formmatedRegistrationForm.email,
              optForSendables: null,
              fullName: response.data.customerFullName,
              loginSource: "registrationForm",
              provider: response.data.provider || 'local',
              isSignup: true,
              gender: response.data?.gender,
              lastPaidCategoryMention: ''
            });

            navigationCallback();
          }
        })
        .catch((error) => {
          if (error.response?.data.status == "captcha_validation") {
            setStickyNotificationDetails((currentDetails) => ({
              show: true,
              title: translator.translate(
                language,
                "default",
                "StickyNotification/danger_title"
              ),
              content: translator.translate(
                language,
                "default",
                "StickyNotification/captcha_failed"
              ),
              level : "error",
              showInfoIcon:true,
              refresh: !currentDetails.refresh
            }))
          }

          else if (error.response?.data.status == "below_age_threshold") {
            setStickyNotificationDetails((currentDetails) => ({
              show: true,
              title: translator.translate(
                language,
                "default",
                "StickyNotification/danger_title"
              ),
              content: translator.translate(
                language,
                "default",
                "StickyNotification/at_least_18"
              ),
              level : "error",
              showInfoIcon:true,
              refresh: !currentDetails.refresh
            }))
          }

          else if (error.response?.data.status === "already_exist") {
            if (onExistingEmail) {
              onExistingEmail();
            } else {
              setStickyNotificationDetails((currentDetails) => ({
                show: true,
                title: translator.translate(
                  language,
                  "default",
                  "StickyNotification/danger_title"
                ),
                content: translator.translate(
                  language,
                  "default",
                  "StickyNotification/email_already_taken"
                ),
                level : "error",
                showInfoIcon:true,
                refresh: !currentDetails.refresh
              }));
            }
          } else {
            setStickyNotificationDetails((currentDetails) => ({
              show: true,
              title: translator.translate(
                language,
                "default",
                "StickyNotification/danger_title"
              ),
              content: "Server Failed",
              level : "error",
              showInfoIcon:true,
              refresh: !currentDetails.refresh
            }))
          }
        });
    } else {
      // do not delete that else
    }
    } catch (error) {
      
      window.clientLogger?.error("RegistrationForm onSubmit error", error);
    } finally {
      setSubmitButtonLoading(false);
    }
  };

  const getOptionsRange = (startRange, finishRange) => {
    let options = [];
    for (let i = startRange; i <= finishRange; i++) {
      options.push({ value: i });
    }

    return options;
  };

  const getCurrentYear = () => {
    let date = new Date();
    let year = date.getFullYear();
    return year;
  };

  const getMonthsOptionsByLanguage = () => {
    let options = [];
    const year = new Date().getFullYear(); // 2020
    const monthList = [...Array(12).keys()]; // [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]
    const formatter = new Intl.DateTimeFormat(language, { month: "long" }); //return month by language,js based class

    monthList.forEach((monthNumber) => {
      options.push({ value: formatter.format(new Date(year, monthNumber)) });
    });

    return options;
  };

  const formatDateToYYYYMMDD = (year, month, day) => {
    return new Date(Date.UTC(year, month-1, day)).toISOString().slice(0, 10);
  };

  return (
    <>
      <style>{`
        #registration-agreement::-webkit-scrollbar,
        #select-day-options::-webkit-scrollbar,
        #select-month-options::-webkit-scrollbar,
        #select-year-options::-webkit-scrollbar {
          width: 3px !important;
        }
        #registration-agreement::-webkit-scrollbar-track,
        #select-day-options::-webkit-scrollbar-track,
        #select-month-options::-webkit-scrollbar-track,
        #select-year-options::-webkit-scrollbar-track {
          background-color: rgba(114, 179, 62, 0.2) !important;
          border-radius: 10px !important;
        }
        #registration-agreement::-webkit-scrollbar-thumb,
        #select-day-options::-webkit-scrollbar-thumb,
        #select-month-options::-webkit-scrollbar-thumb,
        #select-year-options::-webkit-scrollbar-thumb {
          background-color: #72B33E !important;
          border-radius: 10px !important;
        }
      `}</style>
      <div className="registration-form" ref={formContainerRef}>

        {/* HL-10259: Social login buttons hidden for Apple Sign In — the customer is
            already signing up via Apple, showing other login options is confusing. */}
        {!isAppleSignIn && showSocialLogin && (
          <>
            <SocialLoginButtons
             redirectPath={redirectPath}
             cameFromPath={cameFromPath}
             customerVerificationType={customerVerificationType}
             language={language}
             country={country}
             onCompleteSignup={() => navigationCallback()}
             onFailureSignup={() => {     // Show sticky notification error
              setStickyNotificationDetails({
                show: true,
                title: translator.translate(language, "default", "StickyNotification/danger_title"),
                content: translator.translate(language, "default", "StickyNotification/oauth_failed"),
                level: "error",
                showInfoIcon: true,
                refresh: false
              });
             }}
            />

            {/* Divider */}
            <div id="login-form-container-or" className="flex flex-row items-center justify-center gap-[6px] mb-[15px] md:mb-[35px] mt-[24px]">
              <div id="login-form-container-or-line-left" className="h-[1px] bg-[#E5E5E5] flex-1" />
              <span id="login-form-container-or-text" className="text-[12px] font-regular font-poppins text-[#99A3B2] uppercase">{translator.translate(language, "authentication", "RegistrationPage/or")}</span>
              <div id="login-form-container-or-line-right" className="h-[1px] bg-[#E5E5E5] flex-1"/>
            </div>
          </>
        )}

        {/* HL-10259: Apple ID notice shown when the patient arrives from
            Sign in with Apple. The email and name are pre-filled from Apple
            and the customer creates their own password. */}
        {isAppleSignIn && (
          <div className="mb-[16px] p-[16px] bg-[#F0F7FF] rounded-[12px] border border-[#D0E3F7] text-[14px] text-[#1D3A5C] font-poppins text-center">
            Sie melden sich mit Ihrer Apple ID an. Ihre E-Mail-Adresse und Ihr Name wurden von Apple übernommen.
          </div>
        )}


        <div className="flex gap-[15px] mb-[10px] md:mb-[25px]">
          {/* First Name */}
          <InputField
            label={translator.translate(
              language,
              "default",
              "RegistrationForm/first_name"
            )}
            value={registrationFormAttributes.form.first_name}
            errorMsg={
              registrationFormAttributes.errorMessagePerField.first_name
            }
            mandatory
            onChange={(inputValue) =>
              registrationFormAttributes.updateForm("first_name", inputValue)
            }
            language={language}
            onBlur={() => registrationFormAttributes.onFieldBlur("first_name")}
            touched={registrationFormAttributes.touched.first_name}
            disabled={isAppleSignInFirstNameValid}
          />

          {/* Last Name */}
          <InputField
            label={translator.translate(
              language,
              "default",
              "RegistrationForm/surname"
            )}
            value={registrationFormAttributes.form.last_name}
            errorMsg={registrationFormAttributes.errorMessagePerField.last_name}
            mandatory
            onChange={(inputValue) =>
              registrationFormAttributes.updateForm("last_name", inputValue)
            }
            language={language}
            onBlur={() => registrationFormAttributes.onFieldBlur("last_name")}
            touched={registrationFormAttributes.touched.last_name}
            disabled={isAppleSignInLastNameValid}
          />
        </div>

        {/* HL-10259: in Apple Sign In mode the email is pre-filled from Apple and
            shown as read-only so the patient can see it but not edit it. */}
        {!isAppleSignIn && (
          <div className="mb-[10px]">
            {/* Email */}
            <InputField
              id="email"
              type="email"
              label={"E-Mail"}
              value={registrationFormAttributes.form.email}
              errorMsg={registrationFormAttributes.errorMessagePerField.email}
              mandatory
              onChange={(inputValue) =>
                registrationFormAttributes.updateForm("email", inputValue)
              }
              language={language}
              onBlur={() => {
                registrationFormAttributes.onFieldBlur("email");
                const email = registrationFormAttributes.form.email;
                if (email && email.length > 0 && !validateEmail(email, language).isValid) {
                  MainTracker.track("custom", REGISTRATION_INVALID_EMAIL_EVENT, {
                    "Entered email": email,
                  });
                }
              }}
              touched={registrationFormAttributes.touched.email}
            />
          </div>
        )}

        {isAppleSignIn && (
          <div className="mb-[10px]">
            <InputField
              id="email"
              type="email"
              label={"E-Mail"}
              value={registrationFormAttributes.form.email}
              errorMsg={registrationFormAttributes.errorMessagePerField.email}
              mandatory
              language={language}
              disabled
              touched={registrationFormAttributes.touched.email}
            />
          </div>
        )}

        {/* HL-10259: in Apple Sign In mode confirm-email is hidden — email is pre-filled
            from Apple and read-only, so confirming it is unnecessary. */}
        {!isAppleSignIn && (
        <div className="mb-[10px] md:mb-[25px]">
          {/* Confirm Email */}
          <InputField
            id="confirm_email"
            type="email"
            label={translator.translate(
              language,
              "default",
              "LoginQuestion/confirm_email_placeholder"
            )}
            value={registrationFormAttributes.form.confirm_email}
            errorMsg={registrationFormAttributes.errorMessagePerField.confirm_email}
            mandatory
            onChange={(inputValue) =>
              registrationFormAttributes.updateForm("confirm_email", inputValue)
            }
            language={language}
            onBlur={() => {
              registrationFormAttributes.onFieldBlur("confirm_email");
              // Track email mismatch error if emails don't match
              const email = registrationFormAttributes.form.email?.trim().toLowerCase();
              const confirmEmail = registrationFormAttributes.form.confirm_email?.trim().toLowerCase();
              if (confirmEmail && email && email !== confirmEmail) {
                MainTracker.track("custom", EMAIL_MISMATCH_ERROR_EVENT, {});
              }
            }}
            touched={registrationFormAttributes.touched.confirm_email}
          />
        </div>
        )}

        <div id="password-container" className="">
          {/* Password */}
          <InputField
            id="password"
            value={registrationFormAttributes.form.password}
            errorMsg={registrationFormAttributes.errorMessagePerField.password}
            type="password"
            label={translator.translate(
              language,
              "default",
              "RegistrationForm/password"
            )}
            onChange={(inputValue) =>
              registrationFormAttributes.updateForm("password", inputValue)
            }
            language={language}
            mandatory
            onBlur={() => registrationFormAttributes.onFieldBlur("password")}
            touched={registrationFormAttributes.touched.password}
          />

          <p className="text-[#98a0af] text-[12px] text-poppins text-left md:text-right mt-[2px]">
            {translator.translate(
              language,
              "default",
              "RegistrationForm/password_info"
            )}
          </p>
        </div>

        <div id="repeat-password-container" className="mb-[10px] md:mb-[18px] mt-[10px] md:mt-0">
          {/* Repeat Password */}
          <InputField
            value={registrationFormAttributes.form.repeat_password}
            errorMsg={registrationFormAttributes.errorMessagePerField.repeat_password}
            id="repeat_password"
            type="password"
            label={translator.translate(
              language,
              "default",
              "RegistrationForm/repeat_password"
            )}
            mandatory
            onChange={(inputValue) =>
              registrationFormAttributes.updateForm(
                "repeat_password",
                inputValue
              )
            }
            language={language}
            onBlur={() => registrationFormAttributes.onFieldBlur("repeat_password")}
            touched={registrationFormAttributes.touched.repeat_password}
          />


          <p className="text-[#98a0af] text-[12px] text-poppins text-left md:text-right mt-[2px]">
            {translator.translate(
              language,
              "default",
              "RegistrationForm/password_info"
            )}
          </p>
        </div>

        {/* Birthday label*/}
        <div id="registration-birthday-title" className="flex items-center gap-[2px] leading-[24px] mb-[2px] ">
          <span className="font-poppins text-[14px] font-normal text-[#616975]">
            {translator.translate(
              language,
              "default",
              "RegistrationForm/birthday"
            )}
          </span>
          <span className="font-poppins text-[16px] font-normal text-[#CC0000]">*</span>
        </div>


        {birthdayDatePicker ? (
          <div id="registration-birthday-picker-container" className="mb-[25px]">
            <DatePickerWrapper
              id="birthday"
              isBirthdayInput={true}
              localizeWeekdays={true}
              isBodyLevelPortal={false}
              value={registrationFormAttributes.form.year ? `${registrationFormAttributes.form.year}-${registrationFormAttributes.form.month}-${registrationFormAttributes.form.day}` : ""}
              errorMode={!!(registrationFormAttributes.errorMessagePerField.day || registrationFormAttributes.errorMessagePerField.month || registrationFormAttributes.errorMessagePerField.year)}
              onChange={(date) => {
                // The picker emits YYYY-MM-DD; keep the day/month/year fields the submit already assembles.
                const [year = "", month = "", day = ""] = (date || "").split("-");
                registrationFormAttributes.updateFormMultipleAttributes([["year", year], ["month", month], ["day", day]]);
              }}
            />
          </div>
        ) : (
          <div id="registration-birthday-selects-container" className="flex gap-[15px] mb-[25px]">
            <Select
              id={"day"}
              optionsId="select-day-options"
              chevronColor="#72B33E"
              optionHoverColor="rgba(114, 179, 62, 0.2)"
              options={getOptionsRange(1, 31)}
              style={{
                backgroundColor: "var(--primary-white)",
                border: "1px solid #DEE3ED",
                height: 44,
                width: "100%",
                borderRadius: "12px",
              }}
              placeholder={translator.translate(
                language,
                "default",
                "RegistrationForm/day"
              )}
              submitClicked={submitClicked}
              onChangeCallback={(optionObject) =>
                registrationFormAttributes.updateForm(
                  "day",
                  optionObject.option.value
                )
              }
              errorMsg={registrationFormAttributes.errorMessagePerField.day}
            />
            <Select
              id={"month"}
              optionsId="select-month-options"
              chevronColor="#72B33E"
              optionHoverColor="rgba(114, 179, 62, 0.2)"
              options={getMonthsOptionsByLanguage()}
              style={{
                backgroundColor: "var(--primary-white)",
                border: "1px solid #DEE3ED",
                height: 44,
                width: "100%",
                borderRadius: "12px",
              }}
              placeholder={translator.translate(
                language,
                "default",
                "RegistrationForm/month"
              )}
              submitClicked={submitClicked}
              onChangeCallback={(optionObject) =>
                registrationFormAttributes.updateForm(
                  "month",
                  optionObject.index + 1
                )
              }
              errorMsg={registrationFormAttributes.errorMessagePerField.month}
            />
            <Select
              id={"year"}
              optionsId="select-year-options"
              chevronColor="#72B33E"
              optionHoverColor="rgba(114, 179, 62, 0.2)"
              options={getOptionsRange(1910, getCurrentYear() - 18).reverse()}
              style={{
                backgroundColor: "var(--primary-white)",
                border: "1px solid #DEE3ED",
                height: 44,
                width: "100%",
                borderRadius: "12px",
              }}
              placeholder={translator.translate(
                language,
                "default",
                "RegistrationForm/year"
              )}
              submitClicked={submitClicked}
              onChangeCallback={(optionObject) =>
                registrationFormAttributes.updateForm(
                  "year",
                  optionObject.option.value
                )
              }
              errorMsg={registrationFormAttributes.errorMessagePerField.year}
            />
          </div>
        )}

        {/* Birthday label*/}
        <div id="registration-gender-title" className="flex items-center gap-[2px] leading-[24px] mb-[2px] ">
          <span className="font-poppins text-[14px] font-normal text-[#616975]">{translator.translate(language, "default", "RegistrationForm/gender")}</span>
          <span className="font-poppins text-[16px] font-normal text-[#CC0000]">*</span>
        </div>


        <BinarySelectV2025
          errorMsg={registrationFormAttributes.errorMessagePerField.gender}
          valueLeft={translator.translate(
            language,
            "default",
            "RegistrationForm/male"
          )}
          valueRight={translator.translate(
            language,
            "default",
            "RegistrationForm/female"
          )}
          onChange={(genderValue) => {
            if (
              genderValue == "Männlich" ||
              genderValue == "Male" ||
              genderValue == "Homme"
            ) {
              registrationFormAttributes.updateForm("gender", MALE);
            } else {
              registrationFormAttributes.updateForm("gender", FEMALE);
            }
          }}
        />

        {/* if langauge has the checkbox "agree to terms" */}
        {country.toUpperCase() === 'FR' ? (
          <div className="registration-agreement-checkbox-container">
            <Checkbox
              onClick={(inputValue) => {
                if (inputValue.includes("X")) {
                  registrationFormAttributes.updateForm("agree_to_terms", 1);
                } else {
                  registrationFormAttributes.updateForm("agree_to_terms", 0);
                }
              }}
              errorMsg={
                registrationFormAttributes.errorMessagePerField[
                  "agree_to_terms"
                ]
              }
            />

            <span
              className="[&_u]:!no-underline [&_a]:linear-text-color-primary [&_a]:!no-underline"
              dangerouslySetInnerHTML={{
                __html: translator.translate(
                  language,
                  "default",
                  "RegistrationForm/agree_to_terms"
                ).replaceAll(/:urlprefix/g, translator.getCMSUrlPrefixByLocale(`${language}-${country}`)),
              }}
            ></span>
          </div>
        ) : null}
        <div
          id="registration-agreement"
          className="max-h-[120px] overflow-y-scroll font-poppins text-[12px] text-[#616161] leading-[1.5] font-[400] mt-[16px] mb-[24px] [&_u]:!no-underline [&_a]:linear-text-color-primary [&_a]:!no-underline"
          dangerouslySetInnerHTML={{
            __html: translator
              .translate(
                language,
                "default",
                country.toUpperCase() === 'FR' ? "RegistrationForm/agreement_fr" : "RegistrationForm/agreement"
              )
              .replaceAll(/:urlprefix/g, translator.getCMSUrlPrefixByLocale(`${language}-${country}`)),
          }}
        />
        
        { clientConfig.IS_RECAPTCHA_ENABLED &&
        
        <ReCAPTCHA
          sitekey= {clientConfig.RECAPTCHA_SITE_KEY}
          size="invisible"
          ref={reRef}
        />
        }
        
        <SubmitButton
          loading={submitButtonLoading}
          isEnabledState={true}
          onSubmit={onSubmit}
          width={"100%"}
          language={language}
          placeholder={submitButtonPlaceholder}
          fontsize={windowSize < 768 ? "17px" : "20px"}
          fontWeight={"500"}
          className="linear-background-color-primary rounded-[12px] px-0"
        />
      </div>
    </>
  );
}
